import React, { useEffect, useMemo, useState } from "react";
import { TriangleAlert } from "lucide-react";

import { MultiSelectField } from "./ui";
import { listarPreFiltros, resolverPreFiltros } from "../services/preFiltroService";

/**
 * Campo "Pré-filtro" dos painéis de filtro (Repasse, Vendas, Vida do agente).
 *
 * Escolher um grupo preenche banco, convênio e tipo de produto de uma vez; depois dá para ajustar
 * à mão. Quem resolve os vetores é o backend, porque o grupo "restante" depende da lista viva de
 * bancos da Join — e porque combinação que não cabe numa consulta só precisa ser recusada no mesmo
 * lugar em todas as telas.
 */
export default function PreFiltroField({ value = [], onChange, onApply, className = "" }) {
  const [preFiltros, setPreFiltros] = useState([]);
  const [aviso, setAviso] = useState("");

  useEffect(() => {
    let ativo = true;
    listarPreFiltros({ somenteAtivos: true })
      .then((grupos) => {
        if (ativo) setPreFiltros(grupos);
      })
      .catch(() => {
        if (ativo) setPreFiltros([]);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const opcoes = useMemo(
    () => preFiltros.map((grupo) => ({
      value: String(grupo.id),
      label: grupo.restante ? `${grupo.nome} (restante)` : grupo.nome,
    })),
    [preFiltros]
  );

  const selecionar = async (ids) => {
    onChange?.(ids);
    setAviso("");
    if (!ids.length) {
      onApply?.({ bancosIds: [], conveniosIds: [], tiposConvenio: [] });
      return;
    }
    try {
      const vetores = await resolverPreFiltros(ids.map(Number).filter(Boolean));
      if (vetores.incompativel) {
        // Melhor não filtrar e dizer por quê do que devolver um número errado com ar de certo.
        setAviso(vetores.motivo || "Essa combinação não cabe numa consulta só.");
        return;
      }
      onApply?.({
        bancosIds: (vetores.bancosIds || []).map(String),
        conveniosIds: (vetores.conveniosIds || []).map(String),
        tiposConvenio: vetores.tiposConvenio || [],
      });
    } catch (erro) {
      setAviso("Não foi possível aplicar o pré-filtro agora.");
    }
  };

  if (!opcoes.length) return null;

  return (
    <div className={className}>
      <MultiSelectField
        label="Pré-filtro"
        placeholder="Nenhum"
        options={opcoes}
        selectedValues={value}
        onChange={selecionar}
      />
      {aviso ? (
        <p className="mt-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-2 text-xs text-warning">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {aviso}
        </p>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">
          Preenche banco, convênio e tipo de produto do grupo. Dá para ajustar depois.
        </p>
      )}
    </div>
  );
}
